import { api } from "@/services/api";

export interface TransactionResponse {
  data: {
    id: string;
    type: string;
    operation: string;
    operation_label: string;
    amount: number;
    amount_formatted: string;
    description: string;
    created_at: string;
    created_at_formatted: string;
    redirect_url: string;
    reference_uuid: string;
  }[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: {
      url: string | null;
      label: string;
      active: boolean;
    }[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface OverdueReceivableItem {
  id: string;
  type: string;
  entry_type?: string;
  operation: string;
  operation_label: string;
  amount: number;
  amount_formatted: string;
  description: string;
  created_at: string;
  created_at_formatted: string;
  scheduled_release_at_formatted: string;
  redirect_url: string;
  reference_uuid: string;
}

export interface OverdueReceivableResponse {
  data: OverdueReceivableItem[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: {
      url: string | null;
      label: string;
      active: boolean;
    }[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export interface WithdrawalFxPair {
  from: string;
  to: string;
}

export interface WithdrawalFxConfig {
  margin_percent: number;
  rate_source: string;
  manual_rate_usd_brl: number | null;
  supported_pairs: WithdrawalFxPair[];
  conversion_notice: string;
}

export interface WithdrawalFxQuote {
  amount_source: number;
  amount_converted: number;
  market_rate: number;
  market_rate_label: string;
  margin_percent: number;
  margin_multiplier: number;
  conversion_notice: string;
}

export interface WithdrawalFxRecord {
  applied: boolean;
  source_currency?: string;
  payout_currency?: string;
  market_rate?: number;
  market_rate_label?: string;
  margin_percent?: number;
  rate_from?: string;
  rate_to?: string;
  amount_source?: number;
  amount_source_formatted?: string;
  amount_converted?: number;
  amount_converted_formatted?: string;
}

export function hasWithdrawalFxConversion(withdrawal: {
  fx?: WithdrawalFxRecord | null;
}): boolean {
  return Boolean(withdrawal.fx?.applied);
}

export function getWithdrawalListAmountFormatted(withdrawal: {
  amount_requested_formatted?: string;
  fx?: WithdrawalFxRecord | null;
}): string {
  if (withdrawal.fx?.applied && withdrawal.fx.amount_converted_formatted) {
    return withdrawal.fx.amount_converted_formatted;
  }
  return withdrawal.amount_requested_formatted ?? '';
}

export interface WithdrawalResponse {
  data: any[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: {
      url: string | null;
      label: string;
      active: boolean;
    }[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
}

export type TaxFeeItem = {
  id: number;
  paymentMethodId: number;
  percentage: string;
  installments: number;
  fixed: string;
  fixed_fee: string;
  percentage_fee: string;
  type: string;
  type_label: string;
};

export type TaxRetention = {
  retention_days: number;
  retention_percentage: string;
  retention_percentage_formatted: string;
  source?: string;
};

export type TaxPayout = {
  payout_days: number;
};

export type TaxPaymentMethod = {
  payment_method_type_label: string;
  payment_method_type: string;
  payment_method_category_name?: string;
  fees: TaxFeeItem[];
  retention?: TaxRetention;
  payout?: TaxPayout;
};

export type TaxReservedItem = {
  payment_method_type_label: string;
  payment_method_type: string;
  retention_in_days: number;
  retention_percentage: number;
  retention_percentage_formatted: string;
};

export type TaxPayoutItem = {
  payment_method_type_label: string;
  payment_method_type: string;
  payout_days: number;
};

export type TaxesResponse = {
  taxes?: TaxPaymentMethod[];
  reserved?: Record<string, TaxReservedItem | undefined>;
  payouts?: Record<string, TaxPayoutItem | undefined>;
  withdrawal_fee?: {
    percentage_fee: string;
    fixed_fee: number;
    percentage_fee_formatted: string;
    fixed_fee_formatted: string;
  };
};

export interface RetentionResponse {
  items: {
    type: string;
    type_label: string;
    total: number;
    total_formatted: string;
  }[];
}

export interface TotalsWithdrawalsResponse {
  total_amount: string;
  total_amount_formatted: string;
  total_count: number;
  currency?: string;
  conversions?: { from: string; to: string; rate: number }[];
}

export interface ForecastResponse {
  data: {
    id: string;
    amount_formatted: string;
    scheduled_at_formatted: string;
    entry_type?: string;
    entry_type_label?: string;
    order_uuid?: string | null;
    release_amount?: number;
    release_amount_formatted?: string;
    retention_amount?: number;
    retention_amount_formatted?: string;
  }[];
  links: {
    first: string;
    last: string;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number;
    last_page: number;
    links: {
      url: string | null;
      label: string;
      active: boolean;
    }[];
    path: string;
    per_page: number;
    to: number;
    total: number;
  };
  reports: {
    date?: string;
    total?: string;
    entry_type?: string;
    entry_type_label?: string;
    release_amount?: number;
    release_amount_formatted?: string;
    retention_amount?: number;
    retention_amount_formatted?: string;
    scheduled_at_formatted?: string;
  }[];
}

async function getTransactions({
  currentPage,
  q,
  status,
  startDate,
  endDate,
  currency,
}: {
  currentPage?: number;
  q?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  currency: string;
}): Promise<TransactionResponse> {
  const params = new URLSearchParams();

  if (currentPage) {
    params.append("page", currentPage.toString());
  }

  if (q) {
    params.append("q", q);
  }

  if (status && status !== "all") {
    params.append("status", status);
  }

  if (startDate) {
    params.append("start_date", startDate);
  }

  if (endDate) {
    params.append("end_date", endDate);
  }

  if (currency) {
    params.append("currency", currency);
  }

  const response = await api.get(
    `/v1/finances/transactions?${params.toString()}`,
  );
  return response.data;
}

async function getTaxes(
  type: "national" | "international",
  currency: string,
): Promise<TaxesResponse> {
  const response = await api.get(
    `/v1/finances/taxes?type=${type}&currency=${currency || ""}`,
  );
  return response.data;
}

async function getRetention(currency: string): Promise<RetentionResponse> {
  const response = await api.get(
    `/v1/finances/forecasts/all-time?currency=${currency}`,
  );
  
  return {items: response.data.map((item: any) => ({
    type: item.type,
    type_label: item.type_label,
    total: item.total,
    total_formatted: item.total_formatted,
  }))};
}

async function getTotalsWithdrawals(
  currency: string,
  is_dashboard?: boolean,
): Promise<TotalsWithdrawalsResponse> {
  const params = new URLSearchParams({ currency });
  if (is_dashboard) params.append('is_dashboard', 'true');
  const response = await api.get(
    `/v1/reports/withdrawals/totals?${params.toString()}`,
  );
  return response.data;
}

async function getForecasts({
  currentPage,
  start_date,
  end_date,
  currency,
}: {
  currentPage?: number;
  start_date?: string;
  end_date?: string;
  currency: string;
}): Promise<ForecastResponse> {
  const params = new URLSearchParams();

  if (currentPage) {
    params.append("page", currentPage.toString());
  }
  if (start_date) {
    params.append("start_date", start_date);
  }

  if (end_date) {
    params.append("end_date", end_date);
  }

  params.append("currency", currency);

  const response = await api.get(`/v1/finances/forecasts?${params.toString()}`);
  return response.data;
}

async function getWithdrawalFxConfig(): Promise<WithdrawalFxConfig> {
  const response = await api.get("/v1/withdrawals/fx/config");
  return response.data;
}

async function getWithdrawalFxQuote({
  amount_requested,
  currency,
  payout_currency,
}: {
  amount_requested: number;
  currency: string;
  payout_currency: string;
}): Promise<WithdrawalFxQuote> {
  const params = new URLSearchParams({
    amount_requested: amount_requested.toString(),
    currency,
    payout_currency,
  });
  const response = await api.get(`/v1/withdrawals/fx/quote?${params.toString()}`);
  return response.data;
}

async function createWithdrawal(
  amount: number,
  currency: string,
  options?: { type?: string; payout_currency?: string; challenge_token?: string },
): Promise<any> {
  const payload: Record<string, unknown> = {
    amount_requested: amount,
    currency,
    ...(options?.type ? { type: options.type } : {}),
    ...(options?.payout_currency ? { payout_currency: options.payout_currency } : {}),
    ...(options?.challenge_token ? { challenge_token: options.challenge_token } : {}),
  };

  const response = await api.post("/v1/withdrawals", payload);
  return response.data;
}

async function getWithdrawal({
  currentPage,
  currency,
}: {
  currentPage?: number;
  currency: string;
}): Promise<TransactionResponse> {
  const params = new URLSearchParams();

  if (currentPage) {
    params.append("page", currentPage.toString());
  }

  params.append("currency", currency);

  const response = await api.get(`/v1/withdrawals?${params.toString()}`);
  return response.data;
}

async function getOverdueReceivables({
  currency,
  currentPage = 1,
}: {
  currency: string;
  currentPage?: number;
}): Promise<OverdueReceivableResponse> {
  const params = new URLSearchParams();
  if (currency) {
    params.append('currency', currency);
  }
  params.append('page', currentPage.toString());
  const response = await api.get(
    `/v1/finances/transactions/overdues?${params.toString()}`
  );
  return response.data;
}

export const WalletService = {
  getTransactions,
  getTaxes,
  getRetention,
  getTotalsWithdrawals,
  getForecasts,
  getWithdrawalFxConfig,
  getWithdrawalFxQuote,
  createWithdrawal,
  getWithdrawal,
  getOverdueReceivables,
};
